/**
 * Potted — app entry point.
 *
 * UI-001 + UI-002: aim on the cloth (tap to aim, drag to turn: UI-008), adjust by a quarter degree at a time,
 * set power and spin, shoot, and clear the table. Shots are counted, a scratch costs
 * one and puts the cue ball back, and clearing the last ball ends the run.
 *
 * Two rules shape how this file is written:
 *
 *   1. **Nothing reads the future.** `simulateShot` returns the whole shot, and the
 *      rules can score it, before a single frame is drawn. `game/session.ts` holds
 *      that back until the animation clock finishes, and the HUD is only ever updated
 *      from `settle()` (ARCHITECTURE.md §5.1).
 *   2. **The rules live in `sim`, not here.** The Phase 4 Worker replays a shot log
 *      through the same package and must reach the same verdict, so scoring and the
 *      scratch respot are `@potted/sim`'s (rules.ts), never the browser's.
 *
 * Par, the result labels and the share card are Phase 2/3 — they are all relative to
 * a par the solver has not computed yet (CLAUDE.md §12).
 */
import './styles/base.css';
import './styles/table.css';
import './styles/controls.css';
import './styles/game.css';
import './styles/settings.css';
import {
  createTableGeometry,
  createTableSpec,
  CUE_BALL,
  DEFAULT_PHYSICS,
  PHYSICS_VERSION,
  runScore,
  simulateShot,
  stateFromSnapshots,
  type BallId,
  type BallSnapshot,
  type PhysicsConfig,
  type ShotInput,
  type ShotOutcome,
  type SimEvent,
  type SimResult,
  type TableSizeFt,
} from '@potted/sim';
import { firstContact } from './game/aim.ts';
import { nudge, toDirection, type Direction } from './game/direction.ts';
import { aimDragRatio, startAimGesture, type AimGesture } from './game/aim-gesture.ts';
import { createSession } from './game/session.ts';
import { createShotLog } from './game/shot-log.ts';
import { createScene } from './render/scene.ts';
import { createControls } from './ui/controls.ts';
import { createHud } from './ui/hud.ts';
import { createWinScreen, type ResultKind } from './ui/win-screen.ts';
import { createSettingsDialog } from './ui/settings-dialog.ts';
import { readUnlockHint, unlockChange, writeUnlockHint } from './settings/unlock.ts';
import {
  DEFAULT_SETTINGS,
  createSettingsStore,
  type KeyValueStore,
  type Offer,
  type Settings,
} from './settings/settings.ts';
import {
  HOW_TO_PLAY,
  LABELS,
  RESULT_COPY,
  CALENDAR_COPY,
  SETTINGS_COPY,
  SITE,
  STATS_COPY,
  TIMEZONE,
  TODAY_COPY,
  ACCOUNT_COPY,
} from './config/site.ts';
import { accountNetwork } from './ui/account-section.ts';
import { createAccountDialog } from './ui/account-dialog.ts';
import { loadTable } from './daily/load.ts';
import { createHistory, type DayRecord } from './daily/history.ts';
import { playedOnAnotherTable, replayInto, usableRecord } from './daily/resume.ts';
import { CHECKED_KEY, createShotSender } from './daily/send.ts';
import { fetchAccountDays, syncAccountDays } from './daily/account-sync.ts';
import { accountView } from './daily/account-view.ts';
import { effectiveOrientation, isPhone } from './layout/device.ts';
import { runChallenge } from './daily/challenge.ts';
import { describeDayNumbers, fetchDayTotals } from './stats/day-numbers.ts';
import { createHowToPlay } from './ui/how-to-play.ts';
import { firstVisitHint, HINT_SEEN_KEY, mountHint } from './ui/first-visit.ts';
import { createStatsDialog } from './ui/stats-dialog.ts';
import { createCalendarDialog } from './ui/calendar-dialog.ts';
import { chooseArchiveDay, clearArchiveDay } from './calendar/archive-choice.ts';

/** A small calendar page, drawn in the text colour. Markup only: CSP-safe. */
const CALENDAR_ICON =
  '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
  '<rect x="2" y="3" width="12" height="11" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/>' +
  '<rect x="2" y="3" width="12" height="3" fill="currentColor"/>' +
  '<rect x="5" y="1.5" width="1.5" height="3" fill="currentColor"/>' +
  '<rect x="9.5" y="1.5" width="1.5" height="3" fill="currentColor"/></svg>';

/** "1 Oct", for the archive heading. Display only; the day itself came from the server. */
function archiveDate(dateKey: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
  }).format(Date.parse(`${dateKey}T00:00:00Z`));
}
import { computeStats, serverClock } from './stats/stats.ts';

/** A small bar chart, drawn in the text colour. Markup, not a script: CSP-safe. */
/** ACC-008: a head and shoulders, drawn in the button's own colour. */
const ACCOUNT_ICON =
  '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
  '<circle cx="8" cy="5.2" r="3" fill="currentColor"/>' +
  '<path d="M2.2 14.5c0-3.3 2.6-5.3 5.8-5.3s5.8 2 5.8 5.3z" fill="currentColor"/></svg>';

const STATS_ICON =
  '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
  '<rect x="2" y="8" width="3" height="6" fill="currentColor"/>' +
  '<rect x="6.5" y="4" width="3" height="10" fill="currentColor"/>' +
  '<rect x="11" y="6" width="3" height="8" fill="currentColor"/></svg>';
import { newGameUrl, newTableUrl, readLabParams, shareUrl } from './lab.ts';
import { puzzleNumber, shotMark, type ShotMark } from './ui/share.ts';
import { ballAt, legalSpot } from './game/ball-in-hand.ts';
import { createCueCursor, strikeCue } from './audio/cues.ts';
import { spinForTable } from './game/spin.ts';
import { RELEASES } from './config/releases.ts';
import { offeredThemes } from './config/themes.ts';
import { offeredCues } from './config/cues.ts';
import { cueLengthR } from './render/aim-layer.ts';
import { createSoundEngine } from './audio/synth.ts';

/** Query or die. A missing element is a build bug, not something to render around. */
function need<T extends Element>(root: ParentNode, selector: string): T {
  const found = root.querySelector<T>(selector);
  if (found === null) throw new Error(`main: ${selector} is missing from the page`);
  return found;
}

const app = need<HTMLDivElement>(document, '#app');

document.title = `${SITE.name} — ${SITE.tagline}`;

/*
 * DLY-003: which table to play. The server says, by its clock: today's table, or before
 * the first daily one a labelled practice table (daily/table.ts). Debug mode deals from
 * the URL instead (GEN-001) and exists only in local development (D-031):
 * `import.meta.env.DEV` is false in the Cloudflare build, so the whole debug path,
 * panel included, is dropped from what a player downloads. Nothing below runs until
 * there is a table; if there is none, the page says why and waits for a reload.
 */
const lab = readLabParams(window.location.search, Date.now(), import.meta.env.DEV);
const debugRequested = import.meta.env.DEV && lab.debug;
const table = await loadTable(app, lab);
const spec = createTableSpec(table.sizeFt, DEFAULT_PHYSICS.pocketScale);
const geometry = createTableGeometry(spec);
const layout = table.layout;

/** Practice only: the first daily table's date, in Melbourne, e.g. "1 October". */
const practiceNote =
  table.firstRolloverMs === undefined
    ? undefined
    : TODAY_COPY.practiceNote(
        new Intl.DateTimeFormat('en-AU', {
          timeZone: TIMEZONE,
          day: 'numeric',
          month: 'long',
        }).format(table.firstRolloverMs),
      );

app.innerHTML = `
  <main class="stage">
    <header class="stage__header${table.mode === 'archive' ? ' stage__header--archive' : ''}">
      <h1 class="stage__title">${SITE.name}</h1>
      <span class="stage__header-actions">
        <span class="stage__meta numeral"${practiceNote === undefined ? '' : ` title="${practiceNote}"`}>${
          table.mode === 'practice' ? `${TODAY_COPY.practiceTag} · ` : ''
        }${
          // An archive day names its date in place of the size, to fit a phone's header.
          table.mode === 'archive'
            ? `${CALENDAR_COPY.archiveTag} · ${archiveDate(table.id)}`
            : `${String(table.sizeFt)}ft`
        }${table.par === undefined ? '' : ` · ${LABELS.par} ${String(table.par)}`}</span>
        ${
          // Only behind ?debug=1: the tuning popup (DBG-003), and New table, which puts a
          // seed in the address bar and so is the owner's tool, not a player's (URL-001).
          debugRequested
            ? `<button type="button" class="btn btn--quiet" data-debug-toggle aria-expanded="false">Debug</button>
        <button type="button" class="btn btn--quiet" data-new-table>${RESULT_COPY.newTable}</button>`
            : ''
        }
        ${
          // Playing a past day (DLY-005): the way back to today's table, always in view.
          table.mode === 'archive'
            ? `<button type="button" class="btn btn--quiet" data-back-today aria-label="${CALENDAR_COPY.backToTodayLabel}" title="${CALENDAR_COPY.backToTodayLabel}">${CALENDAR_COPY.backToToday}</button>`
            : ''
        }
        <button type="button" class="btn btn--quiet btn--icon" data-calendar
                aria-label="${CALENDAR_COPY.open}" title="${CALENDAR_COPY.open}">${CALENDAR_ICON}</button>
        <button type="button" class="btn btn--quiet btn--icon" data-stats
                aria-label="${STATS_COPY.open}" title="${STATS_COPY.open}">${STATS_ICON}</button>
        <button type="button" class="btn btn--quiet btn--icon" data-help
                aria-label="${HOW_TO_PLAY.open}" title="${HOW_TO_PLAY.open}">?</button>
        <button type="button" class="btn btn--quiet btn--icon" data-settings
                aria-label="${SETTINGS_COPY.open}" title="${SETTINGS_COPY.open}">&#9881;</button>
        <button type="button" class="btn btn--quiet btn--icon account-button" data-account-open
                aria-label="${ACCOUNT_COPY.open}" title="${ACCOUNT_COPY.open}">${ACCOUNT_ICON}</button>
      </span>
    </header>
    <div class="stage__canvas-wrap"><canvas class="stage__canvas"></canvas></div>
    <aside class="panel">
      <div class="hud" data-hud></div>
      <section class="controls" data-controls></section>
    </aside>
  </main>
  <div class="overlay" data-win hidden></div>
`;

const canvas = need<HTMLCanvasElement>(app, '.stage__canvas');
const meta = need<HTMLSpanElement>(app, '.stage__meta');
const scene = createScene(canvas, geometry);

// --- Settings (UI-004) -------------------------------------------------------

/** `localStorage` itself can throw on access (blocked site data), not just its methods. */
function deviceStorage(): KeyValueStore | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/*
 * UI-006: there is no "auto" orientation any more. A first visit gets whichever fits
 * the screen it arrives on, and that choice is saved straight away, so it only ever
 * changes when the player changes it.
 */
/*
 * UI-019: what a player may pick. The supporter pack's themes and cue styles once it is
 * released, and in debug mode, which exists only on the local development server
 * (`debugRequested` needs a dev build), so the owner can see everything before release.
 * THM-004: and for an account the owner has gifted them, as this device last heard from
 * the server; the Account check below puts that right if it has changed.
 */
const giftedHint = readUnlockHint(deviceStorage());
const unlockedAll = RELEASES.supporterPack || debugRequested || giftedHint;
const offer: Offer = { themes: offeredThemes(unlockedAll), cues: offeredCues(unlockedAll) };
const settingsStore = createSettingsStore(
  deviceStorage(),
  {
    ...DEFAULT_SETTINGS,
    orientation: window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape',
  },
  offer,
);
settingsStore.save(settingsStore.load());

/*
 * SND-001: sound effects, made in code. The browser allows sound only after the player
 * touches the page, so every press (re)unlocks it; iOS can suspend audio after a call
 * or a trip to another app, and the next press brings it back.
 */
const sound = createSoundEngine(settingsStore.load().effectsVolume);
// Built while the page is idle, not on the first tap, which is usually a shot (SND-002).
if ('requestIdleCallback' in window) {
  window.requestIdleCallback(() => sound.prepare(), { timeout: 3000 });
} else {
  setTimeout(() => sound.prepare(), 1000);
}
for (const type of ['pointerdown', 'keydown'] as const) {
  window.addEventListener(type, () => sound.unlock(), { capture: true });
}
// A soft tick for buttons and choices. Shoot is left out: it has the cue strike.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const pressed = target?.closest('button, .segmented__option, .theme-option');
    if (pressed != null && !pressed.matches('[data-shoot]')) sound.play({ kind: 'tick', gain: 1 });
  },
  { capture: true },
);

/** Sound for a shot as it plays: each contact when the animation reaches it (rule 5). */
function soundTrack(result: SimResult): (tS: number) => void {
  const cursor = createCueCursor(result.events);
  return (tS) => {
    for (const cue of cursor.advance(tS)) sound.play(cue);
  };
}

/*
 * UI-011: on a phone the table follows how the phone is held, and the layout gets its
 * compact phone form (table.css). Decided once: a phone does not stop being one.
 */
const PHONE = isPhone(
  window.screen.width,
  window.screen.height,
  window.matchMedia('(pointer: coarse)').matches,
);
document.documentElement.dataset['device'] = PHONE ? 'phone' : 'other';

/** The orientation actually drawn: the phone's own, or the player's setting. */
function drawnOrientation(settings: Settings): Settings['orientation'] {
  return effectiveOrientation(settings.orientation, PHONE, window.innerWidth, window.innerHeight);
}

function applySettings(settings: Settings): void {
  const orientation = drawnOrientation(settings);
  // The layout needs to know too: a forced direction changes the shape of the box.
  document.documentElement.dataset['orientation'] = orientation;
  // A theme is a token swap (tokens.css). The id was validated against the theme list
  // on read, so only a known value ever reaches the attribute.
  document.documentElement.dataset['theme'] = settings.theme;
  // UI-019: a cue style is a token swap too, validated against the offered list on read.
  document.documentElement.dataset['cue'] = settings.cueStyle;
  scene.setDisplay({ orientation, fill: settings.tableScale });
  showAimStyle(settings);
  sound.setVolume(settings.effectsVolume);
}

const settingsDialog = createSettingsDialog(
  document.body,
  settingsStore.load(),
  (next) => {
    settingsStore.save(next);
    applySettings(next);
    // The layout may have changed shape; measure again once it has.
    scheduleResize();
  },
  { followsDevice: PHONE, offer },
);

/*
 * ACC-008: Account has its own popup, from a header button beside Settings. The button
 * turns brass while signed in; who that is comes only from the server (/api/me), asked
 * once as the page loads and again whenever the popup opens.
 */
const accountButton = need<HTMLButtonElement>(app, '[data-account-open]');
const accountDialog = createAccountDialog(
  document.body,
  {
    ...accountNetwork,
    // THM-004: signing out takes the gifted themes with it, before the page reloads.
    signOut: async (everywhere) => {
      const result = await accountNetwork.signOut(everywhere);
      if (result.ok) writeUnlockHint(deviceStorage(), false);
      return result;
    },
    reload: () => {
      window.location.reload();
    },
  },
  (state) => {
    // THM-004: the server's answer differs from how this page started: keep it and start
    // again, so the theme list and the saved choice are read with it.
    const changed = unlockChange(state, giftedHint);
    if (changed !== undefined) {
      writeUnlockHint(deviceStorage(), changed);
      window.location.reload();
      return;
    }
    const signedIn = state.kind === 'signed-in';
    accountButton.toggleAttribute('data-signed-in', signedIn);
    const label = signedIn ? ACCOUNT_COPY.openSignedIn : ACCOUNT_COPY.open;
    accountButton.setAttribute('aria-label', label);
    accountButton.title = label;
  },
);
accountButton.addEventListener('click', () => {
  accountDialog.open();
});
// A sign-in that did not finish lands on "/#signin-failed" (ACC-002): say so once, in
// the Account popup, and put the address back to the bare site (URL-001).
if (window.location.hash === '#signin-failed') {
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  accountDialog.notice(ACCOUNT_COPY.signInFailed);
  accountDialog.open();
} else {
  void accountDialog.refresh();
}
// How to play (HTP-001): the rules, the result labels and the shot-colour key.
const howToPlay = createHowToPlay(document.body);
need<HTMLButtonElement>(app, '[data-help]').addEventListener('click', () => {
  howToPlay.open();
});
need<HTMLButtonElement>(app, '[data-settings]').addEventListener('click', () => {
  settingsDialog.open();
});
/* `let`: Practice (DLY-004) starts the table again in a fresh session. */
let session = createSession(layout, geometry, DEFAULT_PHYSICS);

/*
 * DLY-004: the device's record of each daily table (daily/history.ts). Only the daily
 * table's attempt that counts is saved; practice and debug tables never are.
 */
const history = createHistory(deviceStorage());

/*
 * ACC-004: signed in, the account's days come first, BEFORE the send queue and the
 * resume below read the history, so this device continues the account's run today and
 * shows the account's statistics. Not signed in, or slow: nothing changes. Never in the
 * lab, whose tables are not days.
 */
/*
 * ACC-011: signed in, statistics and the calendar are the account's days (account-view.ts),
 * never this device's other days. Undefined when signed out, offline, or in the lab.
 */
let accountDays: readonly DayRecord[] | undefined;
if (table.mode !== 'lab') {
  accountDays = await fetchAccountDays();
  if (accountDays !== undefined) {
    syncAccountDays(history, deviceStorage(), accountDays, table.todayKey);
  }
}

/** The days statistics and the calendar show: the account's when signed in. */
function shownDays(): readonly DayRecord[] {
  return accountDays === undefined
    ? history.all()
    : accountView(accountDays, history, table.todayKey);
}

/*
 * API-004: today's counting shots also go to the server, which plays them itself and
 * keeps the result that counts (D-035). Sent from the history record, so a shot taken
 * offline or before a reload goes later, in order. Silent (D-039): nothing about it is
 * ever shown. Only today's table: never practice, the archive, or a debug table.
 */
/*
 * GEN-011: today's table changed after this device played it (the owner regenerated a
 * published day). The old attempt is set aside by resumeSavedRun; the send queue's count
 * of it is reset here, so the new attempt is sent from its first shot.
 */
if (playedOnAnotherTable(history.get(table.id), table)) {
  try {
    deviceStorage()?.setItem(CHECKED_KEY, JSON.stringify({ dateKey: table.id, sent: 0 }));
  } catch {
    // Blocked storage: the queue starts from nothing anyway.
  }
}

const sender =
  table.mode === 'daily' && table.par !== undefined
    ? createShotSender({
        dateKey: table.id,
        shots: () => history.get(table.id)?.shots ?? [],
        storage: deviceStorage(),
        // SEC-002: a brand-new player passes Turnstile before the server makes them.
        challenge: runChallenge,
        onResult: (result) => {
          adoptServerResult();
          // The server has just counted the clear: the day's numbers can include it.
          if (result.cleared) void showDayNumbers();
        },
      })
    : undefined;
window.addEventListener('online', () => void sender?.flush());

/**
 * API-005: the day's numbers under the counted result. Fetched when the result shows,
 * and again when the server's count of the clear arrives, so the player's own clear is
 * in them (D-039: only as the server counted it).
 */
async function showDayNumbers(): Promise<void> {
  if (sender === undefined || practising) return;
  const totals = await fetchDayTotals(table.id);
  // Practice may have started while that was in flight: its screen gets no numbers.
  if (totals === undefined || practising) return;
  const checked = sender.checked().result;
  winScreen.setDayNumbers(
    describeDayNumbers(totals, checked?.cleared === true ? checked.strokes : undefined),
  );
}

/**
 * If the server's strokes for a cleared table ever differ from the page's, the server's
 * stand (docs/phase-4-plan.md §4.2), and the statistics show them. Determinism makes
 * this a never; this is what happens if it is ever not.
 */
function adoptServerResult(): void {
  const result = sender?.checked().result;
  const record = history.get(table.id);
  if (result?.cleared !== true || record?.strokes === undefined) return;
  if (record.strokes !== result.strokes) history.put({ ...record, strokes: result.strokes });
}

/*
 * STA-002: Statistics, from the top bar and the results screen. Recomputed from the
 * history each time it opens. The countdown runs on the server's clock as reported
 * when the table loaded, so a device clock set wrong cannot move it (D-027).
 */
const countdownTarget = table.nextRolloverMs ?? table.firstRolloverMs;
const statsDialog = createStatsDialog(
  document.body,
  // The server's today, whichever table is on screen: an archive day is not today.
  () => computeStats(shownDays(), table.todayKey),
  countdownTarget === undefined ||
    table.serverNowMs === undefined ||
    table.receivedAtMs === undefined
    ? undefined
    : {
        targetMs: countdownTarget,
        now: serverClock(table.serverNowMs, table.receivedAtMs, () => Date.now()),
        first: table.mode === 'practice',
      },
  () => accountDays !== undefined,
);
need<HTMLButtonElement>(app, '[data-stats]').addEventListener('click', () => {
  statsDialog.open();
});

/*
 * DLY-005: the calendar. Choosing a past day plays it as the archive, remembered for
 * this tab only (calendar/archive-choice.ts) so the address stays bare; choosing today,
 * or "Today's table", goes back. Either way the page reloads and asks the server again.
 */
function playDay(dateKey: string | undefined): void {
  if (dateKey === undefined || dateKey === table.todayKey) clearArchiveDay();
  else chooseArchiveDay(dateKey);
  window.location.reload();
}
const calendarDialog = createCalendarDialog(document.body, {
  todayKey: table.todayKey,
  firstDateKey: table.firstDateKey,
  firstRolloverMs: table.firstRolloverMs,
  playingKey: table.mode === 'daily' || table.mode === 'archive' ? table.id : undefined,
  record: (dateKey) => shownDays().find((d) => d.dateKey === dateKey),
  onPick: playDay,
});
need<HTMLButtonElement>(app, '[data-calendar]').addEventListener('click', () => {
  calendarDialog.open();
});
app.querySelector<HTMLButtonElement>('[data-back-today]')?.addEventListener('click', () => {
  playDay(undefined);
});
/** True once the counting attempt is over and the player chose to practise. */
let practising = false;
/**
 * UI-012: true once the player has closed the results to knock the cue ball around the
 * cleared table. Nothing in free play is saved, sent or scored (see saveProgress).
 */
let freePlay = false;

/*
 * SEC-001. Every shot is recorded as the five fields the Phase 4 Worker will replay —
 * and nothing else. There is no score in here and never will be (PRD N3); the server
 * replays the shots and computes the result itself. Nothing reads the log yet, which
 * is the point: the format is fixed now, while the shots that produce it are being
 * written, rather than reverse-engineered from the UI in Phase 4.
 */
const runIdentity = { seed: table.id, sizeFt: table.sizeFt, ballCount: table.ballCount };
/* `let`: a practice run gets its own log, so it can never mix with the one that counts. */
let shotLog = createShotLog(runIdentity);

/** Debug mode only: deal a new table by changing the URL, so it can be reopened. */
function newTable(): void {
  // Only the seed carries over: it deals its own size and balls (GEN-002).
  const seed = `lab-${Date.now().toString(36)}`;
  window.location.assign(newTableUrl(seed, window.location.href));
}

const hud = createHud(need<HTMLElement>(app, '[data-hud]'));
const winScreen = createWinScreen(need<HTMLElement>(document, '[data-win]'), {
  ...(debugRequested ? { onNewTable: newTable } : {}),
  onClose: enterFreePlay,
  ...(table.mode === 'daily' || table.mode === 'archive'
    ? {
        onPractice: startPractice,
        onStats: () => {
          statsDialog.open();
        },
        onCalendar: () => {
          calendarDialog.open();
        },
      }
    : {}),
});
app.querySelector<HTMLButtonElement>('[data-new-table]')?.addEventListener('click', newTable);

/*
 * The config is a value passed into every shot, not a module-level object, so the
 * debug panel can vary it without anything else in the app knowing (ARCHITECTURE §5).
 */
let physics: PhysicsConfig = DEFAULT_PHYSICS;
let aim: Direction | undefined = openingAim(session.balls());
/* The last shot and the table it was played from, so the debug panel can re-simulate
   rather than replay stale keyframes — a constant tuned since must actually take
   effect, and scrubbing wants the true velocity and spin, which keyframes do not hold. */
let lastShot: SimResult | undefined;
let lastInput: ShotInput | undefined;
let lastStart: readonly BallSnapshot[] | undefined;

const controls = createControls(
  need<HTMLElement>(app, '[data-controls]'),
  DEFAULT_PHYSICS.maxTipOffsetR,
  {
    onNudge(towards) {
      if (aim === undefined || session.phase() !== 'aiming') return;
      // The sense of "left" depends on which way round the table is drawn, and the
      // viewport measures that rather than assuming it (UI-003).
      aim = nudge(aim, towards, scene.viewport().screenTurnSign);
      refreshAim();
    },
    onPower() {
      showAimStyle();
    },
    onSpin() {
      showAimStyle();
    },
    onShoot() {
      shoot();
    },
  },
);

applySettings(settingsStore.load());
scene.resize();
hud.update(0, 0, countRemaining(session.balls()));
redraw();

// UI-022: a device that has never played is told what to do, until its first shot.
const hintSeen = ((): boolean => {
  try {
    return deviceStorage()?.getItem(HINT_SEEN_KEY) === '1';
  } catch {
    return false;
  }
})();
const hintLines = firstVisitHint({
  daysPlayed: history.all().length,
  seen: hintSeen,
  shotsTaken: session.run().shots,
  debug: debugRequested,
  ballCount: countRemaining(session.balls()),
  par: table.par,
});
let dismissHint: (() => void) | undefined =
  hintLines === undefined
    ? undefined
    : mountHint(need<HTMLElement>(app, '.stage__canvas-wrap'), hintLines, () => {
        try {
          deviceStorage()?.setItem(HINT_SEEN_KEY, '1');
        } catch {
          // Blocked storage: the tip may show again next time, which is harmless.
        }
      });

/*
 * Re-render the table at the new size or resolution. Window resize and browser zoom
 * fire `resize`; a PINCH zoom only fires on `visualViewport`, and a move to a screen
 * with a different pixel density fires neither — hence the media query (UI-004).
 * Batched to one per frame, since a pinch fires many events.
 */
let resizePending = false;
function scheduleResize(): void {
  if (resizePending) return;
  resizePending = true;
  requestAnimationFrame(() => {
    resizePending = false;
    // A phone turned sideways, or back: redraw the table the new way round first.
    if (
      PHONE &&
      document.documentElement.dataset['orientation'] !== drawnOrientation(settingsStore.load())
    ) {
      applySettings(settingsStore.load());
    }
    scene.resize();
    // Mid-shot, the scene repaints the frame it is on. Snapping the balls to rest
    // would jump them, and lose a ball that is sinking.
    if (session.phase() !== 'animating') redraw();
    else refreshAim();
  });
}

window.addEventListener('resize', scheduleResize);
window.visualViewport?.addEventListener('resize', scheduleResize);
/*
 * UI-021: the table's box also changes when the page's own layout does, with no window
 * resize at all: the Result button appearing in free play, or a past day's header taking
 * a second row. Unmeasured, the table was squashed into its new box while the cue (on its
 * own layer) was drawn to the old size, so it sat off the ball's line.
 */
if (typeof ResizeObserver === 'function') {
  new ResizeObserver(scheduleResize).observe(canvas);
}

function watchPixelDensity(): void {
  const query = window.matchMedia(`(resolution: ${String(window.devicePixelRatio)}dppx)`);
  query.addEventListener(
    'change',
    () => {
      scheduleResize();
      watchPixelDensity();
    },
    { once: true },
  );
}
watchPixelDensity();

function countRemaining(balls: readonly BallSnapshot[]): number {
  return balls.filter((ball) => ball.id !== CUE_BALL && !ball.potted).length;
}

/** Point at the nearest object ball, so the first shot needs no setup to make sense. */
function openingAim(balls: readonly BallSnapshot[]): Direction | undefined {
  const cue = balls.find((ball) => ball.id === CUE_BALL);
  if (cue === undefined) return undefined;

  let best: Direction | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const ball of balls) {
    if (ball.id === CUE_BALL || ball.potted) continue;
    const dx = ball.xM - cue.xM;
    const dy = ball.yM - cue.yM;
    const distance = dx * dx + dy * dy;
    if (distance >= bestDistance) continue;
    const direction = toDirection(dx, dy);
    if (direction === undefined) continue;
    bestDistance = distance;
    best = direction;
  }
  // No object balls left: aim up the table rather than showing nothing.
  return best ?? toDirection(1, 0);
}

function refreshAim(): void {
  controls.setAim(aim === undefined ? undefined : scene.viewport().bearingDegrees(aim.dx, aim.dy));
  if (aim === undefined || session.phase() !== 'aiming') {
    scene.setAim(undefined);
    return;
  }
  scene.setAim(firstContact(session.balls(), aim.dx, aim.dy, geometry, physics.ballRadiusM));
}

function redraw(): void {
  scene.showAtRest(session.balls());
  refreshAim();
}

// --- Aiming: tap to aim there, drag anywhere to turn (UI-008) -------------------

/** Table coordinates under a pointer. */
function pointerOnTable(event: PointerEvent): { xM: number; yM: number } {
  const rect = canvas.getBoundingClientRect();
  const view = scene.viewport();
  const px = event.clientX - rect.left;
  const py = event.clientY - rect.top;
  return { xM: view.toTableX(px, py), yM: view.toTableY(px, py) };
}

/** The gesture in progress, if a finger is down and aiming. */
let gesture: AimGesture | undefined;

function applyAim(next: Direction | undefined): void {
  if (next === undefined || session.phase() !== 'aiming') return;
  aim = next;
  refreshAim();
}

/*
 * Debug ball in hand (DBG-002, any ball since DBG-005). With it on, a drag that STARTS
 * on a ball picks that ball up and moves it; a drag anywhere else still aims. Off by
 * default and only reachable from the debug panel, which never loads for a player.
 */
let ballInHand = false;
/** The ball being carried, if any. */
let carrying: BallId | undefined;

function placeBallAtPointer(id: BallId, event: PointerEvent): void {
  const at = pointerOnTable(event);
  const spot = legalSpot(session.balls(), id, geometry, physics.ballRadiusM, at.xM, at.yM);
  // An illegal spot (on a ball, in a pocket) leaves the ball where it last was.
  if (spot === undefined) return;
  session.placeBall(id, spot.xM, spot.yM);
  redraw();
}

canvas.addEventListener('pointerdown', (event) => {
  if (session.phase() !== 'aiming') return;
  const cue = session.balls().find((ball) => ball.id === CUE_BALL);
  if (cue === undefined || cue.potted) return;
  canvas.setPointerCapture(event.pointerId);
  const at = pointerOnTable(event);
  if (ballInHand) {
    // Generous grab radius: a fingertip is wider than a ball on a phone.
    carrying = ballAt(session.balls(), at.xM, at.yM, physics.ballRadiusM * 3);
    if (carrying !== undefined) return;
  }
  gesture = startAimGesture(
    aim,
    cue,
    { x: event.clientX, y: event.clientY },
    at,
    // Within two ball widths of the cue ball, the finger's angle swings too wildly to use.
    physics.ballRadiusM * 4,
    // UI-023: slower than the sweep, slowest for a finger.
    aimDragRatio(event.pointerType),
  );
});

canvas.addEventListener('pointermove', (event) => {
  if (carrying !== undefined) placeBallAtPointer(carrying, event);
  else if (gesture !== undefined) {
    applyAim(gesture.move({ x: event.clientX, y: event.clientY }, pointerOnTable(event)));
  }
});

canvas.addEventListener('pointerup', (event) => {
  // A tap aims where it landed; a drag has already turned the aim as it went.
  if (gesture !== undefined) applyAim(gesture.release());
  endGesture(event);
});

function endGesture(event: PointerEvent): void {
  gesture = undefined;
  carrying = undefined;
  if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
}
// A cancelled touch (the browser took it for a scroll, a call came in) is neither.
canvas.addEventListener('pointercancel', endGesture);

// --- Taking the shot --------------------------------------------------------

/** The cue and guide show power and side spin as set (UI-015, UI-016). */
function showAimStyle(settings: Settings = settingsStore.load()): void {
  scene.setAimStyle({
    power: controls.power(),
    cue: settings.showCue,
    sideR: controls.spin().spinX,
    lengthR: cueLengthR(settings.cueLength),
  });
}

function shoot(): void {
  if (session.phase() !== 'aiming' || aim === undefined) return;

  // UI-016: the spin ball is what the player sees; an upright phone shows the table
  // mirrored, so side spin is turned into the table's frame before the shot.
  const spin = spinForTable(controls.spin(), scene.viewport().screenTurnSign);
  const input: ShotInput = {
    dx: aim.dx,
    dy: aim.dy,
    power: controls.power(),
    spinX: spin.spinX,
    spinY: spin.spinY,
  };

  controls.setEnabled(false);
  scene.setAim(undefined);
  hud.flash(undefined);
  lastInput = input;

  // Recorded BEFORE the shot is simulated, so a log entry can never be conditional on
  // how the shot turned out. It also validates, so a shot the server would refuse is
  // rejected here rather than lost at submission (SEC-001).
  shotLog.record(input);
  // Saved before it plays out: a reload mid-shot cannot take back a shot heading for a
  // miss (D-029). The shot log above has already validated it.
  saveProgress();
  // Sent alongside, never waited for: the shot plays out exactly as before (API-004).
  if (!practising && !freePlay) void sender?.flush();
  lastStart = session.balls();

  const startedMs = performance.now();
  const result = session.take(input);
  debugPanel?.reportShot(performance.now() - startedMs, result.durationS);
  lastShot = result;

  sound.play(strikeCue(input.power));
  void scene.play(result, soundTrack(result)).then(() => {
    // `settle()` is the ONLY place the outcome becomes readable — see session.ts.
    finish(session.settle());
  });
}

/*
 * One mark per shot for the share string (SHR-001), recorded as each shot SETTLES, so
 * it can never run ahead of what the player has watched (ARCHITECTURE.md §5.1).
 */
const shotMarks: ShotMark[] = [];

/**
 * SHR-002: the puzzle number, from the table's own date, which the server chose. Daily
 * and archive tables have one; practice and debug tables do not.
 */
const tablePuzzle =
  table.mode === 'daily' || table.mode === 'archive' ? puzzleNumber(table.id) : undefined;

/** The link the results screen shares: the bare site for a player (URL-001). */
function shareLink(): string {
  return shareUrl(lab, window.location.href);
}

function finish(outcome: ShotOutcome): void {
  // UI-022: the tip goes once the first shot is at rest, so the table grows back into
  // its room while nothing is moving on it.
  dismissHint?.();
  dismissHint = undefined;
  // UI-020 (owner, 2026-09-27): every shot starts from centre ball. Spin set for one
  // shot is rarely right for the next, and a forgotten spin spoils it.
  controls.resetSpin();
  const run = session.run();
  if (freePlay) {
    // A free shot: count it on screen, and nothing else. No marks, no result.
    hud.update(run.shots, run.penalties, 0);
    aim = openingAim(session.balls());
    redraw();
    controls.setEnabled(true);
    return;
  }
  shotMarks.push(shotMark(outcome));
  // Only now, with the animation finished, may anything describe what happened —
  // the event log included (ARCHITECTURE.md §5.1).
  if (lastShot !== undefined) debugPanel?.reportEvents(lastShot.events);
  hud.update(run.shots, run.penalties, outcome.ballsRemaining);
  hud.flash(outcome);
  aim = openingAim(session.balls());
  redraw();

  if (session.phase() === 'complete') {
    sound.play({ kind: 'cleared', gain: 1 });
    saveProgress(runScore(run));
    winScreen.show(
      {
        sizeFt: table.sizeFt,
        ballCount: table.ballCount,
        marks: shotMarks,
        score: runScore(run),
        par: table.par,
        puzzleNumber: tablePuzzle,
      },
      run.penalties,
      shareLink(),
      resultKind(),
    );
    // The totals now; again once the server has counted the clear.
    void showDayNumbers();
    return;
  }
  controls.setEnabled(true);
}

// --- Saving, resuming and practice (DLY-004) --------------------------------

/**
 * Save the daily table's attempt that counts: its shots so far, and once cleared, the
 * strokes. Nothing else is ever saved: not practice, not the pre-launch practice
 * tables, not debug tables.
 */
function saveProgress(strokes?: number): void {
  if (table.mode !== 'daily' || practising || freePlay || table.par === undefined) return;
  history.put({
    dateKey: table.id,
    physicsVersion: PHYSICS_VERSION,
    par: table.par,
    ballCount: table.ballCount,
    shots: [...shotLog.shots()],
    ...(strokes === undefined ? {} : { strokes }),
  });
  if (strokes !== undefined) adoptServerResult();
}

/** What the results screen is showing: the attempt that counts, practice, or the archive. */
function resultKind(): ResultKind {
  if (table.mode === 'archive') return 'archive';
  return practising ? 'practice' : 'counted';
}

/** Rack the table up again to practise it. Never saved, and labelled as practice. */
/**
 * UI-012 (owner, 2026-09-27): the results closed, the cleared table stays, and the cue
 * ball can be knocked around it where it came to rest. An endless session, so the empty
 * table never "clears" again. Never saved, never sent to the server, never scored; the
 * result it came from is untouched and comes back with the Result button.
 */
function enterFreePlay(): void {
  // Closing the results again after the Result button: carry on where free play was.
  if (freePlay) return;
  const cue = session.balls().find((ball) => ball.id === CUE_BALL && !ball.potted);
  if (cue === undefined) return;
  freePlay = true;
  session = createSession(
    { ...layout, cue: { xM: cue.xM, yM: cue.yM }, balls: [] },
    geometry,
    physics,
    { endless: true },
  );
  shotLog = createShotLog(runIdentity);
  lastShot = undefined;
  lastInput = undefined;
  hud.setFreePlay(() => {
    winScreen.reopen();
  });
  hud.update(0, 0, 0);
  controls.setEnabled(true);
  aim = openingAim(session.balls());
  redraw();
}

function startPractice(): void {
  practising = true;
  freePlay = false;
  hud.setFreePlay(undefined);
  session = createSession(layout, geometry, physics);
  shotLog = createShotLog(runIdentity);
  shotMarks.length = 0;
  lastShot = undefined;
  lastInput = undefined;
  winScreen.hide();
  hud.update(0, 0, countRemaining(session.balls()));
  hud.flash(undefined);
  // An archive day never counted in the first place, so it keeps its own heading.
  if (table.mode !== 'archive') {
    meta.textContent = `${TODAY_COPY.practiceTag} · ${meta.textContent ?? ''}`;
  }
  controls.resetSpin();
  controls.setEnabled(true);
  aim = openingAim(session.balls());
  redraw();
}

/**
 * Put a daily table back as it was: replay the saved shots instantly (daily/resume.ts).
 * A cleared table goes straight back to its results. A record that will not replay is
 * set aside rather than trusted, and the table starts as dealt.
 */
function resumeSavedRun(): void {
  const saved = usableRecord(history.get(table.id), table);
  if (saved === undefined || saved.shots.length === 0) return;
  let outcomes: ShotOutcome[];
  try {
    outcomes = replayInto(session, saved.shots);
  } catch {
    session = createSession(layout, geometry, physics);
    return;
  }
  for (const shot of saved.shots) shotLog.record(shot);
  for (const outcome of outcomes) shotMarks.push(shotMark(outcome));
  const run = session.run();
  const last = outcomes.at(-1);
  hud.update(run.shots, run.penalties, last?.ballsRemaining ?? countRemaining(session.balls()));
  aim = openingAim(session.balls());
  redraw();
  if (session.phase() === 'complete') {
    // Closed during the last shot's animation: the shot was saved, the finish was not.
    if (saved.strokes === undefined) saveProgress(runScore(run));
    controls.setEnabled(false);
    winScreen.show(
      {
        sizeFt: table.sizeFt,
        ballCount: table.ballCount,
        marks: shotMarks,
        score: runScore(run),
        par: table.par,
        puzzleNumber: tablePuzzle,
      },
      run.penalties,
      shareLink(),
      'counted',
    );
    void showDayNumbers();
  }
}

// --- Debug panel (DBG-001) --------------------------------------------------
// Dynamically imported so none of it reaches the bundle a player downloads.
let debugPanel:
  | {
      reportShot(simMs: number, durationS: number): void;
      reportEvents(events: readonly SimEvent[]): void;
    }
  | undefined;

if (debugRequested) {
  void import('./debug/panel.ts').then(({ createDebugPanel }) => {
    void import('./styles/debug.css');
    debugPanel = createDebugPanel(document.body, {
      onConfigChange(next) {
        physics = next;
        session.usePhysics(next);
        refreshAim();
      },
      onTimeScaleChange(scale) {
        scene.setTimeScale(scale);
      },
      onScrub(fraction) {
        if (lastShot === undefined || lastInput === undefined || lastStart === undefined) return;
        /*
         * Re-simulated to the scrub time rather than interpolated from keyframes.
         * Keyframes hold positions only — by design, since that is all playback and
         * the Worker need — so the vectors would have nothing to draw. Capping the
         * sim clock gives the TRUE state at that instant, velocity and spin included.
         *
         * It costs a whole simulation per scrub tick (a few ms), which is fine for a
         * slider in a tuning tool and would not be fine anywhere else.
         */
        const at = simulateShot(
          stateFromSnapshots(lastStart),
          lastInput,
          { ...physics, maxSimTimeS: Math.max(0, lastShot.durationS * fraction) },
          geometry,
        );
        scene.scrub(at.finalState);
      },

      onVectorsChange(on) {
        scene.setVectors(on);
      },

      sizeFt: table.sizeFt,
      ballCount: table.ballCount,
      trigger: need<HTMLButtonElement>(app, '[data-debug-toggle]'),

      onNewGame(sizeFt: TableSizeFt, ballCount: number) {
        const seed = `lab-${Date.now().toString(36)}`;
        window.location.assign(newGameUrl(seed, sizeFt, ballCount, window.location.href));
      },
      onBallInHandChange(on) {
        ballInHand = on;
      },
      onReplay() {
        /*
         * Replay re-TAKES the shot from wherever the table now is, rather than
         * re-running old keyframes, so a constant tuned since actually takes effect.
         * It costs a shot, exactly as playing it again by hand would — the panel is a
         * tuning tool and must not be a way to un-count a shot (CLAUDE.md §10).
         */
        if (lastInput === undefined || session.phase() !== 'aiming') return;
        const input = lastInput;
        controls.setEnabled(false);
        shotLog.record(input);
        lastStart = session.balls();
        const startedMs = performance.now();
        const again = session.take(input);
        debugPanel?.reportShot(performance.now() - startedMs, again.durationS);
        lastShot = again;
        sound.play(strikeCue(input.power));
        void scene.play(again, soundTrack(again)).then(() => {
          finish(session.settle());
        });
      },
    });
  });
}

// Last, once everything above exists: pick up where this device left today's table.
resumeSavedRun();
// Anything taken before a reload, or offline, that the server has not acknowledged yet.
void sender?.flush();
